//@flow
import * as Immutable from 'immutable';

import { LocationInfo } from 'common/models/flow/LocationInfo';
import type { LocationInfoModelType } from 'common/models/flow/LocationInfo';

import { OutletLocation } from './OutletLocation';
import { LocationId } from './LocationId';

import {
    validatable,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';

@validatable
class InternalLocationInfo extends LocationInfo {
    static fromJS(json: LocationInfoModelType): InternalLocationInfo {
        const state: Object = Object.assign({}, json);
        state.locationId = state.locationId
            ? LocationId.fromJS(state.locationId)
            : state.locationId;
        state.location = state.location
            ? OutletLocation.fromJS(state.location)
            : state.location;
        return new this(Immutable.Map(state));
    }

    get locationId(): ?LocationId {
        return this._state.get('locationId');
    }

    get location(): ?OutletLocation {
        return this._state.get('location');
    }

    @softValidateAll
    hasValidationErrors(): ?boolean {
        const { location, locationId } = this;
        let hasError = false;
        if (!location?.id && !locationId?.locationId) {
            return locationId?.hasValidationErrors();
        }
        if (location) {
            hasError = location.address.hasValidationErrors();
            hasError = location.latLng?.hasValidationErrors();
            if (location.id) {
                hasError = location.getValidationMessage('id');
            }
            return hasError;
        }
        if (locationId && locationId.locationId) {
            hasError = locationId.hasValidationErrors();
            if (location) {
                if (location.formattedAddress) {
                    hasError = location.getValidationMessage(
                        'formattedAddress'
                    );
                }
                if (location.pincode) {
                    hasError = location.getValidationMessage('pincode');
                }
                if (location.address.countryCode) {
                    hasError = location.getValidationMessage('countryCode');
                }
            }
            return hasError;
        }
    }

    @validateAll
    setAllValidationErrors(): this {
        const { location, locationId } = this;
        let updatedLocation = location;
        let updatedLocationId = locationId;
        let updatedAddress = location?.address;
        let updatedLatLng = location?.latLng;
        if (location && location.id) {
            updatedLatLng = location.latLng?.setAllValidationErrors();
            updatedLocation = location
                .setAddress(updatedAddress)
                .setLatLng(updatedLatLng)
                .setAllValidationErrors();
        }
        if (location && locationId && locationId.locationId) {
            updatedLocationId = locationId.setAllValidationErrors();
            updatedAddress = updatedAddress.setCountryCode(
                location.address.countryCode
            );
            updatedAddress = updatedAddress.setPincode(
                location.address.pincode
            );
            if (location.address.formattedAddress) {
                updatedAddress = updatedAddress.setFormattedAddress(
                    location.address.formattedAddress
                );
            }
            if (location.latLng && !location.latLng.isEmpty()) {
                updatedLatLng = updatedLatLng.setAllValidationErrors();
                updatedLocation = updatedLocation.setLatLng(updatedLatLng);
            }

            updatedLocation = updatedLocation.setAddress(updatedAddress);
        }

        if (!location?.id && !locationId?.locationId) {
            updatedLocation = updatedLocation?.setAllValidationErrors();
        }

        return this.setLocationId(updatedLocationId).setLocation(
            updatedLocation
        );
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        let sanitizedLocationInfo = super.defaultSanitize();
        if (sanitizedLocationInfo) {
            let sanitizedLocation = this.location;
            const { locationId: updatedLocationId } = this;
            const { locationId: existingLocationId } = updatedLocationId || {};
            sanitizedLocation = sanitizedLocationInfo.location?.defaultSanitize();
            if (existingLocationId && sanitizedLocation) {
                const sanitizedLatLng = sanitizedLocationInfo.location.latLng?.defaultSanitize();
                sanitizedLocation = sanitizedLocation
                    .setId(existingLocationId)
                    .setCode(existingLocationId)
                    .setLatLng(sanitizedLatLng);
            }
            if (existingLocationId && !sanitizedLocation) {
                return sanitizedLocationInfo.setLocation(null);
            }
            if (!existingLocationId && sanitizedLocation.id) {
                sanitizedLocation = sanitizedLocation.setCode(
                    sanitizedLocation.id
                );
                sanitizedLocationInfo = sanitizedLocationInfo.setLocationId(
                    null
                );
            }
            return sanitizedLocationInfo.setLocation(sanitizedLocation);
        }
        return sanitizedLocationInfo;
    }

    static defaultInit({ props }): InternalLocationInfo {
        const locationId = LocationId.defaultInit({ props });
        const location = OutletLocation.defaultInit({ props });
        const updatedLocation = location.setType(null);
        return InternalLocationInfo.fromJS({})
            .setLocationId(locationId)
            .setLocation(updatedLocation);
    }
}

export { InternalLocationInfo as LocationInfo };
